import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Sparkles, RefreshCw } from "lucide-react";

const STATUS_PILL = {
  new: "bg-sky-50 text-sky-700 border-sky-200",
  qualified: "bg-amber-50 text-amber-800 border-amber-200",
  unqualified: "bg-slate-100 text-slate-600 border-slate-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
  archived: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

const SOURCE_LABEL = {
  contact_form: "Contact Form",
  custom_design: "Custom Design",
  referral: "Referral",
  direct: "Direct",
  import: "Import",
  admin_manual: "Manual",
  other: "Other",
};

const STATUS_TABS = ["all", "new", "qualified", "converted", "lost"];

const AdminLeads = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [status, setStatus] = useState(params.get("status") || "all");
  const [source, setSource] = useState(params.get("source") || "any");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    display_name: "", primary_email: "", primary_phone: "",
    lead_source: "admin_manual", source_details: "", country: "IN", notes: "",
  });

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 400); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = { limit: 100 };
      if (debouncedQ) p.q = debouncedQ;
      if (status !== "all") p.status = status;
      if (source !== "any") p.source = source;
      const { data } = await api.get("/admin/leads", { params: p });
      setItems(data.items || []); setTotal(data.total || 0);
      setStatusCounts(data.status_counts || {});
    } catch (err) {
      toast.error("Failed to load leads", { description: err?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [debouncedQ, status, source]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const n = new URLSearchParams();
    if (q) n.set("q", q);
    if (status !== "all") n.set("status", status);
    if (source !== "any") n.set("source", source);
    setParams(n, { replace: true });
  }, [q, status, source, setParams]);

  const submitCreate = async () => {
    if (!form.display_name.trim()) return toast.error("Name is required");
    setCreating(true);
    try {
      const body = { ...form };
      if (!body.primary_email) delete body.primary_email;
      if (!body.primary_phone) delete body.primary_phone;
      if (!body.source_details) delete body.source_details;
      if (!body.notes) delete body.notes;
      const { data } = await api.post("/admin/leads", body);
      toast.success("Lead created", { description: data.lead_no });
      setShowCreate(false);
      setForm({ display_name: "", primary_email: "", primary_phone: "", lead_source: "admin_manual", source_details: "", country: "IN", notes: "" });
      navigate(`/admin/leads/${data.id}`);
    } catch (err) {
      toast.error("Could not create lead", { description: err?.response?.data?.detail || "" });
    } finally { setCreating(false); }
  };

  const runBackfill = async () => {
    if (!window.confirm("Scan all existing enquiries and attach any un-linked ones to a Client or a new Lead?")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/leads/_migrate/backfill-from-enquiries");
      toast.success("Backfill complete", {
        description: `Scanned ${data.scanned} · Created ${data.created_leads} leads · Attached ${data.attached_to_client} to clients · Skipped ${data.skipped_no_email} (no email)`,
      });
      load();
    } catch (err) {
      toast.error("Backfill failed", { description: err?.response?.data?.detail || "" });
    } finally { setBackfilling(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-leads-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="leads-page-title">Leads</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every new enquiry becomes a Lead. Qualify and Convert to a Client with one click — no duplicate records, complete timeline preserved.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="backfill-leads-btn" onClick={runBackfill} disabled={backfilling}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${backfilling ? "animate-spin" : ""}`} />
              {backfilling ? "Running…" : "Backfill from Enquiries"}
            </Button>
            <Button data-testid="new-lead-btn" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> New Lead
            </Button>
          </div>
        </div>

        {/* Status pill row */}
        <div className="flex flex-wrap gap-2" data-testid="lead-status-tabs">
          {STATUS_TABS.map((s) => {
            const n = s === "all" ? total : (statusCounts[s] || 0);
            const active = status === s;
            return (
              <button
                key={s}
                data-testid={`lead-status-tab-${s}`}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border rounded-full transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50 border-slate-200 text-slate-600"}`}
              >
                {s}
                <span className={`ml-2 font-mono ${active ? "text-white/90" : "text-slate-400"}`}>{n}</span>
              </button>
            );
          })}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="leads-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, lead-no, email, phone…" className="pl-9" />
              </div>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="leads-source-filter" className="w-52"><SelectValue placeholder="Any source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any source</SelectItem>
                  {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pt-0" data-testid="leads-list">
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading leads…</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="leads-empty-state">
                No leads match this filter.{" "}
                <button onClick={() => setShowCreate(true)} className="underline">Create one manually</button>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <tr>
                      <th className="text-left py-3 pr-3">Lead</th>
                      <th className="text-left py-3 pr-3">Contact</th>
                      <th className="text-left py-3 pr-3">Source</th>
                      <th className="text-left py-3 pr-3">Enquiries</th>
                      <th className="text-left py-3 pr-3">Status</th>
                      <th className="text-left py-3 pr-3">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b last:border-b-0 hover:bg-slate-50">
                        <td className="py-3 pr-3">
                          <Link to={`/admin/leads/${l.id}`} className="font-medium hover:underline" data-testid={`lead-link-${l.id}`}>
                            {l.display_name}
                          </Link>
                          <div className="text-xs text-muted-foreground font-mono">{l.lead_no}</div>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{l.primary_email || "—"}</div>
                          <div className="text-xs text-muted-foreground">{l.primary_phone || ""}</div>
                        </td>
                        <td className="py-3 pr-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-slate-50 border border-slate-200 rounded">
                            <Sparkles className="h-3 w-3" /> {SOURCE_LABEL[l.lead_source] || l.lead_source}
                          </span>
                        </td>
                        <td className="py-3 pr-3 font-mono text-xs">{l.enquiry_count || 0}</td>
                        <td className="py-3 pr-3">
                          <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider border rounded ${STATUS_PILL[l.status] || STATUS_PILL.new}`}>
                            {l.status}
                          </span>
                          {l.converted_to_client_id ? (
                            <Link to={`/admin/clients/${l.converted_to_client_id}`} className="ml-2 text-xs underline text-emerald-700">→ Client</Link>
                          ) : null}
                        </td>
                        <td className="py-3 pr-3 text-xs text-muted-foreground">{l.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pt-3 text-xs text-muted-foreground">{items.length} of {total} lead{total === 1 ? "" : "s"}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-lead-dialog">
          <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display name *</Label>
              <Input data-testid="create-lead-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Priya Sharma or Acme Homes" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input data-testid="create-lead-email" type="email" value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="create-lead-phone" value={form.primary_phone} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={form.lead_source} onValueChange={(v) => setForm({ ...form, lead_source: v })}>
                  <SelectTrigger data-testid="create-lead-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="create-lead-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IN">India</SelectItem>
                    <SelectItem value="US">USA</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="AU">Australia</SelectItem>
                    <SelectItem value="GB">UK</SelectItem>
                    <SelectItem value="CH">Switzerland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Source details</Label>
              <Input data-testid="create-lead-source-details" value={form.source_details} onChange={(e) => setForm({ ...form, source_details: e.target.value })} placeholder="Referred by …, Trade show 2026, etc." />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="create-lead-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="submit-lead-create" onClick={submitCreate} disabled={creating}>{creating ? "Creating…" : "Create Lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLeads;
